import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BodegaSale } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { EmptyState, TableSkeleton } from '../components/States';
import { useLoad } from '../lib/useAsync';
import { bogotaDay, formatDate, formatMoney } from '../lib/format';
import type { Language } from '../i18n';

/** Suma días a una fecha de Bogotá (AAAA-MM-DD). */
function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Vista "Facturas de la Bodega": cada venta que hizo la Bodega —directa a un cliente o a un local—
 * con su detalle de líneas y a quién se le vendió. Solo lectura (pedido del cliente 07-10-2026).
 */
export function WarehouseSales() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user } = useAuth();
  const money = (n: number) => formatMoney(n, lang);
  const today = bogotaDay(new Date());

  const [from, setFrom] = useState(shiftDay(today, -29));
  const [to, setTo] = useState(today);
  const [range, setRange] = useState({ from: shiftDay(today, -29), to: today });
  const [detail, setDetail] = useState<BodegaSale | null>(null);

  const data = useLoad(() => (user ? api.warehouseSales({ from: range.from, to: range.to }) : Promise.resolve(null)), [user, range.from, range.to]);
  const sales = useMemo(() => data.data?.sales ?? [], [data.data]);
  const totals = useMemo(
    () => sales.reduce((a, s) => ({ direct: a.direct + (s.kind === 'DIRECT' ? s.total : 0), internal: a.internal + (s.kind === 'INTERNAL' ? s.total : 0) }), { direct: 0, internal: 0 }),
    [sales],
  );

  const apply = () => setRange({ from, to });

  return (
    <Panel
      title={t('warehouseSales.title')}
      description={t('warehouseSales.subtitle')}
      badge={<Badge tone="blue">{money(totals.direct + totals.internal)}</Badge>}
    >
      <div className="toolbar">
        <label htmlFor="wsFrom">{t('warehouseSales.from')}</label>
        <input id="wsFrom" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        <label htmlFor="wsTo">{t('warehouseSales.to')}</label>
        <input id="wsTo" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
        <button type="button" onClick={apply}>{t('warehouseSales.apply')}</button>
      </div>

      <div className="summary-row">
        <div><span className="muted">{t('warehouseSales.directTotal')}</span> <strong>{money(totals.direct)}</strong></div>
        <div><span className="muted">{t('warehouseSales.internalTotal')}</span> <strong>{money(totals.internal)}</strong></div>
      </div>

      {data.loading ? (
        <TableSkeleton cols={5} />
      ) : sales.length === 0 ? (
        <EmptyState title={t('warehouseSales.empty')} />
      ) : (
        <table className="table" id="wsSales">
          <thead>
            <tr>
              <th>{t('warehouseSales.cols.doc')}</th>
              <th>{t('warehouseSales.cols.date')}</th>
              <th>{t('warehouseSales.cols.kind')}</th>
              <th>{t('warehouseSales.cols.buyer')}</th>
              <th className="num">{t('warehouseSales.cols.total')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sales.map((s) => (
              <tr key={s.document_id} data-doc={s.doc_no}>
                <td>{s.doc_no}</td>
                <td>{formatDate(s.created_at, lang, false)}</td>
                <td><Badge tone={s.kind === 'DIRECT' ? 'green' : 'blue'}>{t(`warehouseSales.kind.${s.kind}`)}</Badge></td>
                <td>{s.buyer || t('warehouseSales.anonymous')}</td>
                <td className="num">{money(s.total)}</td>
                <td><button type="button" className="link" onClick={() => setDetail(s)}>{t('warehouseSales.seeLines')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail ? (
        <Modal open title={`${detail.doc_no} · ${detail.buyer || t('warehouseSales.anonymous')}`} onClose={() => setDetail(null)}>
          <table className="table" id="wsLines">
            <thead>
              <tr>
                <th>{t('warehouseSales.cols.ref')}</th>
                <th>{t('warehouseSales.cols.product')}</th>
                <th className="num">{t('warehouseSales.cols.qty')}</th>
                <th className="num">{t('warehouseSales.cols.price')}</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((l, i) => (
                <tr key={`${l.ref}-${i}`}>
                  <td>{l.ref}</td>
                  <td>{l.name}</td>
                  <td className="num">{l.qty}</td>
                  <td className="num">{money(l.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      ) : null}
    </Panel>
  );
}
